// Language + locale primitives shared by the edge functions (Deno) and the
// frontend (Vite). No dictionaries here, so `src/lib/utils.ts` can import the
// locale tag without dragging every namespace into the base chunk.
//
// Scope reminder (docs/i18n/plan.md): these are the languages LightMaster
// speaks to the contractor's END CLIENT. The contractor-facing app stays
// English.

export const SUPPORTED_LANGUAGES = ['en', 'es', 'fr'] as const;
export type Lang = (typeof SUPPORTED_LANGUAGES)[number];

export const DEFAULT_LANGUAGE: Lang = 'en';

/** BCP-47 tags used for Intl formatting. Canadian French was the request. */
export const LOCALE_TAG: Record<Lang, string> = {
  en: 'en-US',
  es: 'es-US',
  fr: 'fr-CA',
};

/** Contractor-facing (English UI) labels for the language pickers. */
export const LANGUAGE_LABELS: Record<Lang, string> = {
  en: 'English',
  es: 'Spanish (Español)',
  // Canadian French, matching LOCALE_TAG — said out loud in the picker because
  // a Quebec customer asked whether the translations were "European standards".
  fr: 'French — Canadian (Français canadien)',
};

export function isLanguage(value: unknown): value is Lang {
  return typeof value === 'string' && (SUPPORTED_LANGUAGES as readonly string[]).includes(value);
}

/**
 * Anything that is not exactly one of the supported codes collapses to the
 * fallback — a NULL customer override, a stale payload, a typo from an API
 * caller. Case-insensitive on purpose ("FR" from a form is still French).
 */
export function normalizeLanguage(value: unknown, fallback: Lang = DEFAULT_LANGUAGE): Lang {
  if (typeof value !== 'string') return fallback;
  const lower = value.trim().toLowerCase();
  return isLanguage(lower) ? lower : fallback;
}

export function localeTag(lang: unknown): string {
  return LOCALE_TAG[normalizeLanguage(lang)];
}

export function formatMoney(lang: unknown, amount: number, currency = 'USD'): string {
  const value = Number.isFinite(amount) ? amount : 0;
  return new Intl.NumberFormat(localeTag(lang), { style: 'currency', currency }).format(value);
}

export type DateStyle = 'short' | 'medium' | 'long';

/**
 * A date-only string ('YYYY-MM-DD', a Postgres `date`) is parsed as LOCAL
 * midnight so a due date never renders as the previous day in a negative-UTC
 * zone. Full timestamps go through the native parser as instants.
 */
function toDate(input: string | Date): Date {
  if (input instanceof Date) return input;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(input);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return new Date(input);
}

export function formatDate(
  lang: unknown,
  input: string | Date,
  style: DateStyle = 'medium',
  options: Intl.DateTimeFormatOptions = {},
): string {
  const date = toDate(input);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(localeTag(lang), { dateStyle: style, ...options }).format(date);
}
